import { useState } from "react";
import { PaymentElement, useStripe, useElements } from "@stripe/react-stripe-js";
import { Bluelagoon } from "@bluelagoon";
import { Button } from "@shadcn-in-nx/ui/button";
import { Loader2 } from "lucide-react";
import { useTranslation } from "react-i18next";

const LATAM_HOURLY_RATE = 75;

export function SupportCheckoutForm({
    selectedPlan,
    isYearly,
    isLatam,
    userId,
    paymentMethods = [],
    onSuccess,
    onCancel,
}) {
    const { t } = useTranslation();
    const stripe = useStripe();
    const elements = useElements();
    const [isProcessing, setIsProcessing] = useState(false);
    const [error, setError] = useState(null);
    const [paymentMode, setPaymentMode] = useState(paymentMethods.length > 0 ? "existing" : "new");
    const [selectedPaymentMethodId, setSelectedPaymentMethodId] = useState(
        paymentMethods.find((pm) => pm.isDefault)?.id || paymentMethods[0]?.id || null
    );

    const basePrice = isLatam ? selectedPlan.hours * LATAM_HOURLY_RATE : selectedPlan.price;
    const monthlyPrice = isYearly ? Math.round(basePrice * 0.8) : basePrice;
    const displayPrice = isYearly ? monthlyPrice * 12 : monthlyPrice;

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!stripe || !elements) return;

        setIsProcessing(true);
        setError(null);

        try {
            let paymentMethodId = selectedPaymentMethodId;

            if (paymentMode === "new") {
                const { error: submitError } = await elements.submit();
                if (submitError) { setError(submitError.message); setIsProcessing(false); return; }

                const { error: setupError, setupIntent } = await stripe.confirmSetup({
                    elements,
                    confirmParams: { return_url: window.location.href },
                    redirect: "if_required",
                });

                if (setupError) { setError(setupError.message); setIsProcessing(false); return; }
                paymentMethodId = setupIntent.payment_method;

                await Bluelagoon.setDefaultPaymentMethod({ userId, paymentMethodId });
            }

            const response = await Bluelagoon.createSupportSubscription({
                userId,
                tier: selectedPlan.tier,
                isYearly,
                isLatam,
                paymentMethodId,
            });

            const responseData = response?.data?.data || response?.data;

            if (responseData?.requiresAction && responseData?.clientSecret) {
                const { error: confirmError, paymentIntent } = await stripe.confirmCardPayment(
                    responseData.clientSecret,
                    { payment_method: paymentMethodId }
                );

                if (confirmError) {
                    setError(confirmError.message || t("billing.supportPlans.authFailed"));
                    setIsProcessing(false);
                    return;
                }

                if (paymentIntent.status === "succeeded") {
                    onSuccess();
                } else {
                    setError(`${t("billing.supportPlans.paymentNotCompleted")} ${paymentIntent.status}`);
                }
            } else if (response?.status === 200) {
                onSuccess();
            } else {
                setError(response?.error || response?.data?.error || t("billing.supportPlans.failedToCreate"));
            }
        } catch (err) {
            setError(err.message || t("billing.supportPlans.paymentFailed"));
        } finally {
            setIsProcessing(false);
        }
    };

    return (
        <form onSubmit={handleSubmit} className="space-y-6">
            <OrderSummary plan={selectedPlan} price={displayPrice} isYearly={isYearly} isLatam={isLatam} t={t} />

            {paymentMethods.length > 0 && (
                <PaymentModeToggle mode={paymentMode} onChange={setPaymentMode} t={t} />
            )}

            {paymentMode === "existing" ? (
                <ExistingPaymentMethods methods={paymentMethods} selected={selectedPaymentMethodId} onSelect={setSelectedPaymentMethodId} t={t} />
            ) : (
                <div className="border rounded-lg p-4">
                    <PaymentElement options={{ layout: "tabs" }} />
                </div>
            )}

            {error && <p className="text-red-600 text-sm">{error}</p>}

            <div className="flex gap-3">
                <Button type="button" variant="outline" onClick={onCancel} className="flex-1">
                    {t("common.cancel")}
                </Button>
                <Button type="submit" disabled={isProcessing || !stripe} className="flex-1 bg-blue-600 hover:bg-blue-700">
                    {isProcessing ? <Loader2 className="w-4 h-4 animate-spin" /> : t("billing.supportPlans.pay", { amount: displayPrice })}
                </Button>
            </div>
        </form>
    );
}

function OrderSummary({ plan, price, isYearly, isLatam, t }) {
    return (
        <div className="bg-gray-50 rounded-lg p-4">
            <h3 className="font-semibold mb-2">{plan.name}</h3>
            <p className="text-sm text-muted-foreground mb-3">{t("billing.supportPlans.hoursOfSupport", { hours: plan.hours })}</p>
            {isLatam && <p className="text-green-600 text-sm mb-2">🌎 {t("billing.supportPlans.latamApplied")}</p>}
            <div className="flex justify-between items-center border-t pt-3">
                <span className="font-medium">{isYearly ? t("billing.supportPlans.totalYearly") : t("billing.supportPlans.totalMonthly")}</span>
                <span className="text-xl font-bold">${price}</span>
            </div>
        </div>
    );
}

function PaymentModeToggle({ mode, onChange, t }) {
    return (
        <div className="flex gap-2">
            <button type="button" onClick={() => onChange("existing")}
                className={`flex-1 py-2 px-4 rounded-lg border ${mode === "existing" ? "border-blue-500 bg-blue-50" : "border-gray-200"}`}>
                {t("billing.supportPlans.savedCard")}
            </button>
            <button type="button" onClick={() => onChange("new")}
                className={`flex-1 py-2 px-4 rounded-lg border ${mode === "new" ? "border-blue-500 bg-blue-50" : "border-gray-200"}`}>
                {t("billing.supportPlans.newCard")}
            </button>
        </div>
    );
}

function ExistingPaymentMethods({ methods, selected, onSelect, t }) {
    return (
        <div className="space-y-2">
            {methods.map((pm) => (
                <button key={pm.id} type="button" onClick={() => onSelect(pm.id)}
                    className={`w-full p-3 rounded-lg border text-left flex items-center gap-3 ${selected === pm.id ? "border-blue-500 bg-blue-50" : "border-gray-200"}`}>
                    <span>💳</span>
                    <span>•••• {pm.last4}</span>
                    {pm.isDefault && <span className="text-xs bg-gray-200 px-2 py-0.5 rounded">{t("billing.supportPlans.default")}</span>}
                </button>
            ))}
        </div>
    );
}
